import * as Sentry from '@sentry/react';
import { stableHashWeb, type SentryTagContext } from '@shared/shared/hash';
import { hasAnalyticsConsent, syncConsentToWindow } from './lib/consent';

const ENV = (import.meta.env.PROD ? 'production' : 'local');
const DSN = (import.meta.env as any)?.VITE_SENTRY_DSN || '';
const enableAnalytics = String((import.meta as any)?.env?.VITE_ENABLE_ANALYTICS) === 'true';
const TRACES = Number(((import.meta as any)?.env?.VITE_SENTRY_TRACES ?? 0.2));
const REPLAYS = Number(((import.meta as any)?.env?.VITE_SENTRY_REPLAYS ?? 0));

// Ensure hash salt is available at runtime for shared hasher fallback
try {
  (window as any).__SENTRY_HASH_SALT__ = (import.meta as any)?.env?.VITE_SENTRY_HASH_SALT || '';
} catch {}

// Sync consent to window before computing consent state
syncConsentToWindow();

// Initialize Sentry always for error capture; gate BrowserTracing/Replay by enableAnalytics + consent
try {
  if (DSN) {
    const consentOn = hasAnalyticsConsent();
  Sentry.init({
      dsn: DSN,
    environment: ENV,
    release: ((import.meta as any)?.env?.VITE_APP_VERSION as any) || undefined,
      tracesSampleRate: enableAnalytics && consentOn ? (TRACES || 0) : 0,
      replaysSessionSampleRate: enableAnalytics && consentOn ? (REPLAYS || 0) : 0,
    integrations: [
        (enableAnalytics && consentOn ? (Sentry.browserTracingIntegration?.() as any) : undefined),
        (enableAnalytics && consentOn ? ((Sentry as any).replayIntegration?.({ maskAllText: true, blockAllMedia: true })) : undefined),
      (Sentry as any).captureConsoleIntegration?.({ levels: ['error','warn'] }),
    ].filter(Boolean),
    beforeSend(event) {
      try {
        const level = (event as any)?.level as string | undefined;
        const logger = (event as any)?.logger as string | undefined;
        const msg = (event as any)?.message as string | undefined;
        const reqUrl = (event as any)?.request?.url || '';
        if (level && /^(info|log)$/i.test(level)) return null;
        if (logger === 'console' && (!level || /^(info|log)$/i.test(level))) return null;
        if (typeof msg === 'string' && msg.includes('[ws]')) return null;
          // Strip query strings from request URL
          if ((event as any).request) {
            try {
              const raw = String((event as any).request.url || '');
              const u = new URL(raw, window.location.origin);
              (event as any).request.url = u.pathname;
            } catch { delete (event as any).request; }
        }
        // Ensure no user object is sent
        if (event.user) delete (event as any).user;
        // Drop large extras that might contain payloads
        if (event.extra && typeof event.extra === 'object') {
          const extra = event.extra as Record<string, unknown>;
            for (const k of ['payload', 'file_content', 'csv_preview', 'request_body', 'response_body']) {
            if (k in extra) delete (extra as any)[k];
          }
        }
      } catch {}
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      try {
        if (breadcrumb && breadcrumb.category === 'http') {
          const data: any = breadcrumb.data || {};
          const urlRaw = String(data.url || '');
          try {
            const u = new URL(urlRaw, window.location.origin);
            breadcrumb.data = { method: data.method, url: u.pathname };
          } catch {
            breadcrumb.data = { method: data.method, url: urlRaw.split('?')[0] };
          }
        }
      } catch {}
      return breadcrumb;
    },
      ignoreErrors: [
        /ResizeObserver loop limit exceeded/i,
        /NetworkError when attempting to fetch resource/i,
        /Request aborted/i,
        /Non-Error exception captured/,
      ],
      denyUrls: [/extensions\//i, /^chrome:\/\//i],
  });
  }
} catch {}

export async function setSentryContext(ctx: SentryTagContext): Promise<void> {
  try {
    Sentry.setTags(
      Object.fromEntries(
        Object.entries({
          user_id_hash: ctx.userIdHash || undefined,
          project_id_hash: ctx.projectIdHash || undefined,
          plot_id: ctx.plotId || undefined,
          plot_type: ctx.plotType || undefined,
          app_version: ctx.appVersion || (import.meta.env?.VITE_APP_VERSION as any) || undefined,
          deployment: ctx.deployment || 'web',
          environment: ENV,
        }).filter(([, v]) => v != null && v !== '')
      )
    );
  } catch {}
}

export async function setSentryContextFromRaw(raw: { userId?: string | null; projectId?: string | null; plotId?: string | null; plotType?: string | null }): Promise<void> {
  try {
    const [userIdHash, projectIdHash] = await Promise.all([
      raw.userId ? stableHashWeb(raw.userId) : Promise.resolve<string | null>(null),
      raw.projectId ? stableHashWeb(raw.projectId) : Promise.resolve<string | null>(null),
    ]);
    await setSentryContext({ userIdHash, projectIdHash, plotId: raw.plotId || null, plotType: raw.plotType || null });
  } catch {}
}

export function clearSentryContext(): void {
  try {
    Sentry.setTag('user_id_hash', 'anon');
    Sentry.setTag('project_id_hash', 'none');
    Sentry.setTag('plot_id', 'none');
    Sentry.setTag('plot_type', 'none');
  } catch {}
}

export function setSentryUser(user: { id?: string; email?: string | null }): void {
  try {
    // Tag by hashed id only for privacy; do not attach raw email
    const id = user?.id || undefined;
    if (id) {
      Sentry.setUser({ id });
    }
  } catch {}
}

// Re-export common helpers for app usage
export const captureException = Sentry.captureException;
export const startSpan = Sentry.startSpan;

// Dev-only helper to test error reporting
if (import.meta.env.DEV) {
  (window as any).__sentryTestFrontend = () => {
    throw new Error('Sentry test (frontend)');
  };
}


